import { CopyOutlined } from "@ant-design/icons";
import { Descriptions, Divider, Modal, notification, Spin, Tag } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IAlunoCustomDTO,
  IAlunoTurmaHistoricoCleanDTO,
} from "../../../../models/happy-code-api.model";
import { useAlunoService } from "../../../../services/aluno.service";
import { useTurmaService } from "../../../../services/turma.service";
import { formatDateToPattern } from "../../../../util/format-date.util";
import { getAge } from "../../../../util/util";
import { IAlunoTable } from "../../interfaces/aluno-table.interface";
import "./styles.css";

interface AlunoModalProps {
  open: boolean;
  onClose: () => void;
  aluno: IAlunoTable | null;
  idTurma: number;
}

export default function AlunoModal({ open, onClose, aluno, idTurma }: AlunoModalProps) {
  const { t } = useTranslation(["gestao_de_aulas"]);
  const alunoService = useAlunoService();
  const turmaService = useTurmaService();

  const [alunoDetalhe, setAlunoDetalhe] = useState<IAlunoCustomDTO | null>(null);
  const [historico, setHistorico] = useState<IAlunoTurmaHistoricoCleanDTO | null>(null);

  const [loading, setLoading] = useState(false);
  const [hasFetchError, setHasFetchError] = useState(false);

  useEffect(() => {
    if (!open || !aluno?.codigo || !idTurma) return;

    const idAluno = Number(aluno.codigo.replace("#", ""));
    if (!idAluno) return;

    setLoading(true);
    setHasFetchError(false);
    setAlunoDetalhe(null);
    setHistorico(null);

    Promise.allSettled([
      alunoService.findResponsavelAluno(idAluno),
      turmaService.findEntradaTurmaByAlunoETurma(idAluno, idTurma),
    ])
      .then(([respAluno, respHist]) => {
        if (respAluno.status === "fulfilled") {
          setAlunoDetalhe(respAluno.value.data);
        } else {
          setHasFetchError(true);
        }

        if (respHist.status === "fulfilled") {
          setHistorico(respHist.value.data);
        }
      })
      .finally(() => setLoading(false));
  }, [open, aluno?.codigo, idTurma]);

  const handleCopy = async (text?: string) => {
    if (!text) return;

    await navigator.clipboard.writeText(text);
    notification.success({
      message: t("gestao_de_aulas:studentModal.itemCopied"),
    });
  };

  return (
    <Modal
      title={t("gestao_de_aulas:studentModal.title")}
      open={open}
      onCancel={onClose}
      onOk={onClose}
      okText={t("gestao_de_aulas:studentModal.close")}
      cancelButtonProps={{ style: { display: "none" } }}
      okButtonProps={{style: {background: '#1a4da1'}}}
      destroyOnHidden
      loading={loading}
    >
      {hasFetchError && (
        <div className="aluno-modal__fetch-error">
          {t("gestao_de_aulas:studentModal.fetchError")}
        </div>
      )}
      <Descriptions bordered column={1} size="middle" className="aluno-modal__descriptions">
        <Descriptions.Item label={t("gestao_de_aulas:studentModal.fullName")}>
          {alunoDetalhe?.pessoa?.nome ?? "-"}
        </Descriptions.Item>

        <Descriptions.Item label={t("gestao_de_aulas:studentModal.age")}>
          {getAge(alunoDetalhe?.pessoa?.dataNascimento) ?? "-"}
        </Descriptions.Item>

        <Descriptions.Item label={t("gestao_de_aulas:studentModal.gender")}>
          {alunoDetalhe?.pessoa?.genero?.descricao ?? "-"}
        </Descriptions.Item>

        <Descriptions.Item label={t("gestao_de_aulas:studentModal.status")}>
          <Tag color={alunoDetalhe?.ativo ? "green" : "red"}>
            {alunoDetalhe?.ativo
              ? t("gestao_de_aulas:studentModal.active")
              : t("gestao_de_aulas:studentModal.inactive")}
          </Tag>
        </Descriptions.Item>

        {historico?.dataEntrada && (
          <Descriptions.Item label={t("gestao_de_aulas:studentModal.classEntryDate")}>
            {formatDateToPattern(historico.dataEntrada) ?? "-"}
          </Descriptions.Item>
        )}

        <Descriptions.Item label={t("gestao_de_aulas:studentModal.guardian")}>
          {alunoDetalhe?.pessoa?.pessoaResponsaveis?.[0]?.responsavel?.nome ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("gestao_de_aulas:studentModal.guardianContacts")}>
          {alunoDetalhe?.pessoa?.pessoaResponsaveis?.[0]?.responsavel?.contatos?.length
            ? alunoDetalhe.pessoa.pessoaResponsaveis[0].responsavel.contatos.map((contato, i) => (
              <div key={i} style={{ display: "flex", gap: 6, padding: 1 }}>
                <span style={{ wordBreak: 'break-word' }}>{contato.descricao}</span>
                <CopyOutlined
                  style={{ color: "#1a4da1", cursor: "pointer" }}
                  title={t("gestao_de_aulas:studentModal.copy")}
                  onClick={() => void handleCopy(contato?.descricao)}
                />
              </div>
            ))
            : "-"}
        </Descriptions.Item>
      </Descriptions>
    </Modal>
  );
}
